/* eslint-disable */
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import moment from 'moment';
import '../../components/skeletonRow.css';
import './AdsReport.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faHome,
  faCloudUploadAlt,
  faDownload,
  faSync,
  faCalendarAlt,
  faBullhorn,
  faTimes,
} from '@fortawesome/free-solid-svg-icons';
import { API_URL } from '../../config';
import { showToast } from '../../components/toast';
import { PermissionWrapper } from '../../helpers/permission';
import AdsReportStats from './AdsReportStats';
import AdsReportCharts from './AdsReportCharts';
import AdsPlatformSummaryTable from './AdsPlatformSummaryTable';
import AdsReportImportModal from './AdsReportImportModal';

const defaultDateRange = {
  startDate: moment().subtract(29, 'days').format('YYYY-MM-DD'),
  endDate: moment().format('YYYY-MM-DD'),
};

const AdsReport = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // View Mode: defaults to 'platform' as requested
  const [viewMode] = useState('platform');

  const [loading, setLoading] = useState(false);
  const [recalculating, setRecalculating] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);

  // Date Range & Campaign Filter State (Top Right)
  const [dateRange, setDateRange] = useState(defaultDateRange);
  const [selectedCampaign, setSelectedCampaign] = useState('');
  const [filterOptions, setFilterOptions] = useState({ platforms: [], campaigns: [], months: [] });

  // Summary & Chart Data (contains platforms and campaignGroups arrays)
  const [summaryData, setSummaryData] = useState({ kpi: {}, charts: { platforms: [], campaignGroups: [] } });


  // Fetch filter dropdown options
  const fetchFilterOptions = useCallback(async () => {
    try {
      const token = localStorage.getItem('authToken');
      const res = await axios.get(`${API_URL}report-ads/filter-options`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.data?.success) {
        setFilterOptions(res.data.data);
      }
    } catch (err) {
      console.error('Failed to load filter options', err);
    }
  }, []);

  // Fetch KPI & Charts Summary Data
  const fetchSummary = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('authToken');
      const payload = {
        startDate: dateRange.startDate || undefined,
        endDate: dateRange.endDate || undefined,
        campaign: selectedCampaign || undefined,
        groupBy: viewMode === 'campaign' ? 'campaign' : 'platform',
      };

      const res = await axios.post(`${API_URL}report-ads/summary`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (res.data?.success) {
        setSummaryData(res.data.data);
        if (res.data.data.filterOptions) {
          setFilterOptions(res.data.data.filterOptions);
        }
      }
    } catch (err) {
      console.error('Failed to fetch summary data', err);
      showToast.error('Failed to load Ads Report summary analytics.');
    } finally {
      setLoading(false);
    }
  }, [dateRange, selectedCampaign, viewMode]);

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
    const contentEl = document.getElementById('content');
    if (contentEl) contentEl.scrollTop = 0;
    const pageContainer = document.getElementById('page-container');
    if (pageContainer) pageContainer.scrollTop = 0;
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }, []);

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  const hasAnyFilter = () => {
    return (
      dateRange.startDate !== defaultDateRange.startDate ||
      dateRange.endDate !== defaultDateRange.endDate ||
      selectedCampaign !== ''
    );
  };

  // Reset Filters
  const handleClearFilters = () => {
    setDateRange(defaultDateRange);
    setSelectedCampaign('');
  };

  // Export CSV Handler
  const handleExportCSV = async () => {
    try {
      setExportLoading(true);
      const token = localStorage.getItem('authToken');
      const payload = {
        startDate: dateRange.startDate || undefined,
        endDate: dateRange.endDate || undefined,
        campaign: selectedCampaign || undefined,
      };

      const res = await axios.post(`${API_URL}report-ads/export`, payload, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });

      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `all_platforms_ads_report_${moment().format('YYYYMMDD_HHmm')}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      showToast.success('Ads report exported successfully!');
    } catch (err) {
      console.error('Export error', err);
      showToast.error('Failed to export Ads report.');
    } finally {
      setExportLoading(false);
    }
  };

  // Click on a platform or campaign to navigate to detail page
  const handleItemClick = (itemName) => {
    if (itemName) {
      if (viewMode === 'campaign') {
        navigate(`/Report/adsReport/campaign/${encodeURIComponent(itemName)}`);
      } else {
        navigate(`/Report/adsReport/${encodeURIComponent(itemName)}`);
      }
    }
  };

  // Refresh All Data
  const refreshAllData = () => {
    fetchFilterOptions();
    fetchSummary();
  };

  // Recalculate Ads Report metrics from CRM
  const handleRecalculate = async () => {
    try {
      setRecalculating(true);
      const token = localStorage.getItem('authToken');
      const res = await axios.post(
        `${API_URL}backendApi/recalculate-ads-report`,
        {},
        {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        },
      );

      if (res.data?.success) {
        showToast.success(res.data.message || 'Ads report recalculated successfully!');
        refreshAllData();
      } else {
        showToast.error(res.data?.message || 'Failed to recalculate Ads report.');
      }
    } catch (err) {
      console.error('Recalculate error', err);
      showToast.error(err.response?.data?.message || 'Failed to recalculate Ads report.');
    } finally {
      setRecalculating(false);
    }
  };

  const isCampaignView = viewMode === 'campaign';
  const displayList = isCampaignView
    ? summaryData.charts?.campaignGroups || []
    : summaryData.charts?.platforms || [];

  return (
    <div id="page-container" className="page-header-fixed page-sidebar-fixed fade in">
      <div id="content">
        {/* Top Header Row 1: Breadcrumbs on Left and Actions & Campaign Filter on Right */}
        <div className="row pb-2 align-items-center">
          <div className="col-auto">
            <ul className="breadcrumb" style={{ margin: 0, padding: 0, background: 'transparent' }}>
              <li className="breadcrumb-item">
                <a href="/dashboard" style={{ textDecoration: 'none' }}>
                  <FontAwesomeIcon icon={faHome} /> Home
                </a>
              </li>
              <li className="breadcrumb-item">Reports</li>
              <li className="breadcrumb-item active">Ads Report</li>
            </ul>
          </div>

          {/* Top Right Actions & Campaign Filter - First Row */}
          <div className="col d-flex justify-content-end align-items-center gap-2">
            <button
              type="button"
              className="btn btn-warning btn-sm"
              style={{
                height: '31px',
                whiteSpace: 'nowrap',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                fontWeight: '600',
              }}
              onClick={() => navigate('/Report/adsReport/campaign-update')}
              title="Go to Campaign Update"
            >
              <FontAwesomeIcon icon={faBullhorn} />
              <span>Campaign Update</span>
            </button>

            <PermissionWrapper module="report_ads_recalculate" action="view">
              <button
                type="button"
                className="btn btn-primary btn-sm"
                style={{
                  height: '31px',
                  whiteSpace: 'nowrap',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  fontWeight: '600',
                }}
                onClick={handleRecalculate}
                disabled={recalculating}
                title="Recalculate / Sync Ads Report from Google Sheet"
              >
                <FontAwesomeIcon icon={faSync} spin={recalculating} />
                <span>{recalculating ? 'Recalculating...' : 'Recalculate'}</span>
              </button>
            </PermissionWrapper>

            <select
              className="form-control"
              style={{
                minWidth: '240px',
                maxWidth: '420px',
                height: '31px',
                padding: '2px 8px',
                fontSize: '12px',
                borderRadius: '4px',
                cursor: 'pointer',
                backgroundColor: '#fff',
                borderColor: selectedCampaign ? '#007bff' : '#ced4da',
                fontWeight: selectedCampaign ? '600' : '400',
              }}
              value={selectedCampaign}
              onChange={(e) => setSelectedCampaign(e.target.value)}
            >
              <option value="">-- All Campaigns --</option>
              {filterOptions.campaigns?.map((c, idx) => {
                const val = typeof c === 'object' ? c.campaign : c;
                const label =
                  typeof c === 'object'
                    ? (c.label || (c.campaign_name ? `${c.campaign} - ${c.campaign_name}` : c.campaign))
                    : c;
                return (
                  <option key={idx} value={val}>
                    {label}
                  </option>
                );
              })}
            </select>

            {selectedCampaign && (
              <button
                className="btn btn-default btn-sm"
                style={{ height: '31px', padding: '2px 10px', fontSize: '12px' }}
                onClick={() => setSelectedCampaign('')}
                title="Clear Campaign Filter"
              >
                <FontAwesomeIcon icon={faTimes} className="me-1 text-danger" /> Clear Filter
              </button>
            )}
          </div>
        </div>

        {/* Row 2: Date Range Filter - Second Row */}
        <div className="row pb-3">
          <div className="col-12 d-flex justify-content-end align-items-center gap-2">
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                background: '#fff',
                border: '1px solid #ced4da',
                borderRadius: '4px',
                padding: '3px 8px',
                gap: '6px',
                height: '31px',
              }}
            >
              <FontAwesomeIcon icon={faCalendarAlt} style={{ color: '#6c757d', fontSize: '12px' }} />
              <span style={{ fontSize: '11.5px', fontWeight: '600', color: '#495057' }}>From:</span>
              <input
                type="date"
                value={dateRange.startDate}
                onChange={(e) => setDateRange((prev) => ({ ...prev, startDate: e.target.value }))}
                style={{ border: 'none', outline: 'none', fontSize: '12px', background: 'transparent', padding: '0 2px' }}
              />
              <span style={{ fontSize: '11.5px', fontWeight: '600', color: '#495057' }}>To:</span>
              <input
                type="date"
                value={dateRange.endDate}
                onChange={(e) => setDateRange((prev) => ({ ...prev, endDate: e.target.value }))}
                style={{ border: 'none', outline: 'none', fontSize: '12px', background: 'transparent', padding: '0 2px' }}
              />
              {loading && <FontAwesomeIcon icon={faSync} spin style={{ color: '#007bff', fontSize: '11px', marginLeft: '2px' }} title="Loading..." />}
            </div>
          </div>
        </div>

        {/* Section 1: Overall KPI Statistics Counters */}
        <AdsReportStats kpi={summaryData.kpi} loading={loading} />

        {/* Section 2: Visual Analytics Charts */}
        <AdsReportCharts charts={summaryData.charts} loading={loading} viewMode={viewMode} />

        {/* Section 3: Action & Control Bar above Summary Table */}
        <div
          className="panel-heading d-flex justify-content-between align-items-center"
          style={{ padding: '8px 0', borderBottom: 'none' }}
        >
          {/* Left: Table Title & Clear Filters */}
          <div className="d-flex align-items-center gap-2">
            <h4 className="panel-title" style={{ fontSize: '14px', fontWeight: '600', margin: 0 }}>
              {isCampaignView ? 'Campaign Mapping Summary' : 'Platform Performance Summary'} ({displayList.length})
            </h4>

            {hasAnyFilter() && (
              <button
                className="btn btn-default btn-xs"
                style={{ marginLeft: '10px' }}
                onClick={handleClearFilters}
                title="Clear all filters"
              >
                Clear Filter
              </button>
            )}
          </div>

          {/* Right: Refresh, export */}
          <div className="d-flex align-items-center">
            <span style={{ fontSize: '0.8rem' }} className="me-3">
              {loading ? (
                <span className="skeleton-text" style={{ width: '120px', display: 'inline-block' }}></span>
              ) : (
                `Showing ${displayList.length} ${isCampaignView ? 'campaigns' : 'platforms'}`
              )}
            </span>

            <button className="btn btn-default btn-sm mx-1" onClick={refreshAllData} title="Refresh Data">
              <FontAwesomeIcon icon={faSync} />
            </button>

            <button
              className="btn btn-default btn-sm mx-1"
              onClick={handleExportCSV}
              disabled={exportLoading || displayList.length === 0}
              title="Export Report"
            >
              {exportLoading ? (
                <>
                  <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                  Exporting...
                </>
              ) : (
                <>
                  <FontAwesomeIcon icon={faDownload} className="me-1" /> Export Report
                </>
              )}
            </button>
          </div>
        </div>

        {/* Section 4: Summary Table (1 Row Per Platform / Campaign with Clickable Drilldown & Bulk Update) */}
        <AdsPlatformSummaryTable
          platforms={displayList}
          loading={loading}
          onPlatformClick={handleItemClick}
          groupBy={viewMode}
          onCampaignUpdated={refreshAllData}
        />

        {/* Import Modal */}
        <AdsReportImportModal
          isOpen={isImportModalOpen}
          onClose={() => setIsImportModalOpen(false)}
          onImportSuccess={() => {
            refreshAllData();
          }}
        />
      </div>
    </div>
  );
};

export default AdsReport;
